import React from "react";
import {
  MousePointer2,
  Minus,
  MoveUpRight,
  ArrowUpRight,
  Square,
  Hand,
  Trash2,
  Eraser,
  Layers,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  Waves,
  GitBranch,
  PanelsTopLeft,
  PenTool,
} from "lucide-react";
import { C } from "../../theme/tokens";
import { getToolbarStrings } from "./toolbarLocales";

// lucide-react has no plain single vertical-line glyph (the closest,
// ChevronsUpDown, reads as a resize/expand affordance, not a drawing tool).
// This mirrors Minus's own geometry (a single centered stroke) just turned
// 90°, so it matches the rest of the icon set's weight and style exactly.
function VerticalLineIcon({ size = 24, strokeWidth = 2, ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      <line x1="12" y1="3" x2="12" y2="21" />
    </svg>
  );
}

// Tools are grouped so the panel reads as sections instead of one flat
// grid — helps people scan for what they want instead of hunting through
// nine identical-weight icons in a row. Labels aren't hardcoded here — they
// come from the active locale's dictionary, keyed by group/tool id.
const TOOL_GROUPS = [
  {
    key: "lines",
    tools: [
      { id: "horizontal-line", Icon: Minus },
      { id: "vertical-line", Icon: VerticalLineIcon },
      { id: "ray", Icon: ArrowUpRight },
      { id: "trend-line", Icon: MoveUpRight },
    ],
  },
  {
    key: "fib",
    tools: [
      { id: "fib-retracement", Icon: SlidersHorizontal },
      { id: "fib-speed-fan", Icon: GitBranch },
    ],
  },
  {
    key: "shapes",
    tools: [
      { id: "parallel-channel", Icon: PanelsTopLeft },
      { id: "rectangle", Icon: Square },
      { id: "andrews-pitchfork", Icon: Waves },
    ],
  },
];

const ALL_TOOLS = TOOL_GROUPS.flatMap((group) => group.tools);

function toolMeta(id, t) {
  if (id === "select") return { label: t.select, Icon: MousePointer2 };
  if (id === "pan") return { label: t.pan, Icon: Hand };
  const tool = ALL_TOOLS.find((item) => item.id === id);
  return tool ? { label: t.tools[id], Icon: tool.Icon } : null;
}

function ToolButton({ active, disabled, label, Icon, onClick, danger = false, size = 40 }) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        border: `1px solid ${active ? C.violet : "transparent"}`,
        borderRadius: 10,
        background: active
          ? `linear-gradient(135deg, ${C.violetDeep}, rgba(139,107,246,.18))`
          : "transparent",
        color: disabled
          ? C.mutedA
          : active
            ? C.violetBright
            : danger
              ? "#C98270"
              : C.secondary,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        transition: "background .15s ease, border-color .15s ease, color .15s ease, transform .1s ease",
        WebkitTapHighlightColor: "transparent",
      }}
      onPointerDown={(event) => {
        if (disabled) return;
        event.currentTarget.style.transform = "scale(0.92)";
      }}
      onPointerUp={(event) => {
        event.currentTarget.style.transform = "scale(1)";
      }}
      onPointerLeave={(event) => {
        event.currentTarget.style.transform = "scale(1)";
      }}
    >
      <Icon size={17} strokeWidth={1.75} />
    </button>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        padding: "6px 2px 3px",
        color: C.mutedA,
        fontSize: 9,
        fontWeight: 800,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </div>
  );
}

// Vertical rule used to separate groups when the toolbar runs horizontally
// (mobile bottom bar) instead of the full-width divider used in the
// vertical sidebar.
function VDivider() {
  return (
    <div
      style={{
        flex: "0 0 auto",
        width: 1,
        alignSelf: "stretch",
        margin: "4px 2px",
        background: C.hairline,
      }}
    />
  );
}

function Divider() {
  return (
    <div
      style={{
        gridColumn: "1 / -1",
        width: "100%",
        height: 1,
        margin: "5px 0",
        background: C.hairline,
      }}
    />
  );
}

// Indicators is the one control that needs a flyout. It's used from both
// the vertical sidebar and the horizontal bottom bar, and in both cases the
// flyout is positioned with `fixed` coordinates computed from the trigger
// button's own rect — that way it always escapes whatever `overflow`
// clipping the scrollable toolbar container has, instead of getting cut off.
//
// It closes on an outside pointerdown, and — while open — repositions
// itself on scroll/resize instead of closing. An earlier version closed on
// any `scroll` event captured at the window, but `scroll` doesn't bubble,
// only propagates through the capture phase, so it still reached a window
// listener registered with `capture: true`. On mobile the toolbar itself is
// a horizontally scrollable strip, so simply tapping the button (or the
// browser nudging the tapped element into view) fired a scroll on that
// inner strip, which the capture listener caught and closed the popover
// before it was ever visible.
function IndicatorsControl({ indicators, onIndicator, openUpward, t }) {
  const [open, setOpen] = React.useState(false);
  const [coords, setCoords] = React.useState(null);
  const triggerRef = React.useRef(null);
  const flyoutRef = React.useRef(null);

  const computeCoords = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords(
      openUpward
        ? { left: rect.left, bottom: window.innerHeight - rect.top + 8 }
        : { left: rect.right + 8, top: rect.top }
    );
  }, [openUpward]);

  const toggle = () => {
    if (!open) computeCoords();
    setOpen((value) => !value);
  };

  React.useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (
        triggerRef.current?.contains(event.target) ||
        flyoutRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    };

    let raf = null;
    const handleReposition = () => {
      if (raf != null) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        computeCoords();
      });
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
      if (raf != null) cancelAnimationFrame(raf);
    };
  }, [open, computeCoords]);

  return (
    <div ref={triggerRef} style={{ position: "relative", flex: "0 0 auto" }}>
      <ToolButton active={open} label={t.indicators} Icon={Layers} onClick={toggle} />

      {open && coords && (
        <div
          ref={flyoutRef}
          style={{
            position: "fixed",
            left: coords.left,
            top: coords.top,
            bottom: coords.bottom,
            zIndex: 40,
            width: 168,
            padding: 6,
            border: `1px solid ${C.hairline}`,
            borderRadius: 11,
            background: "rgba(11,14,10,.97)",
            boxShadow: "0 16px 36px rgba(0,0,0,.5)",
            backdropFilter: "blur(12px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "5px 7px 7px",
              color: C.mutedB,
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: ".08em",
              textTransform: "uppercase",
            }}
          >
            {t.indicators} <ChevronRight size={12} />
          </div>

          {["EMA20", "SMA20"].map((id) => {
            const enabled = indicators.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => onIndicator(id, !enabled)}
                style={{
                  width: "100%",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${
                    enabled ? "rgba(139,107,246,.28)" : "transparent"
                  }`,
                  borderRadius: 7,
                  background: enabled ? C.violetDeep : "transparent",
                  color: enabled ? C.violetBright : C.primary,
                  cursor: "pointer",
                  fontSize: 11,
                  textAlign: "left",
                }}
              >
                <span>{id}</span>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: enabled ? C.violetBright : C.mutedA,
                  }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ChartToolbar({
  activeTool,
  onTool,
  onDeleteSelected,
  onClear,
  hasSelection,
  indicators,
  onIndicator,
  wide = false,
  // Same "ru" | "uk" | "en" value the rest of the app already uses (App.jsx's
  // `lang` state, sourced from the backend's user.language). Falls back to
  // Ukrainian if omitted or set to something unsupported.
  lang,
}) {
  // Mobile starts collapsed to a slim bar so it doesn't cost the chart any
  // vertical space until the person actually wants the tools. Desktop is
  // always expanded — there's a dedicated sidebar column for it now, so it
  // never has to overlap the chart.
  const [expanded, setExpanded] = React.useState(wide);

  React.useEffect(() => {
    setExpanded(wide);
  }, [wide]);

  const t = React.useMemo(() => getToolbarStrings(lang), [lang]);
  const active = toolMeta(activeTool, t);

  // ---- Desktop: vertical sidebar, lives beside the chart (not on top of
  // it), scrolls internally so it can never get clipped by a short chart. ----
  if (wide) {
    return (
      <div
        style={{
          width: 148,
          flex: "0 0 auto",
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          boxSizing: "border-box",
          padding: 6,
          borderRight: "1px solid rgba(42,58,31,.95)",
          background: "rgba(11,14,10,.92)",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <ToolButton
            label={t.select}
            Icon={MousePointer2}
            active={activeTool === "select"}
            onClick={() => onTool("select")}
          />
          <ToolButton
            label={t.pan}
            Icon={Hand}
            active={activeTool === "pan"}
            onClick={() => onTool("pan")}
          />
          <IndicatorsControl indicators={indicators} onIndicator={onIndicator} t={t} />

          {TOOL_GROUPS.map((group) => (
            <React.Fragment key={group.key}>
              <SectionLabel>{t.groups[group.key]}</SectionLabel>
              {group.tools.map(({ id, Icon }) => (
                <ToolButton
                  key={id}
                  active={activeTool === id}
                  label={t.tools[id]}
                  Icon={Icon}
                  onClick={() => onTool(id)}
                />
              ))}
            </React.Fragment>
          ))}

          <Divider />

          <ToolButton
            label={hasSelection ? t.deleteSelected : t.deleteSelectedDisabled}
            Icon={Trash2}
            disabled={!hasSelection}
            danger
            onClick={onDeleteSelected}
          />
          <ToolButton label={t.clearAll} Icon={Eraser} danger onClick={onClear} />
        </div>
      </div>
    );
  }

  // ---- Mobile: horizontal bar docked below the chart. Collapsed it's a
  // slim strip; expanded it grows and the chart (flex: 1 in the parent)
  // shrinks to make room — the panel never sits on top of the candles. ----
  if (!expanded) {
    const FabIcon = active?.Icon ?? PenTool;
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        style={{
          flex: "0 0 auto",
          width: "100%",
          height: 40,
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "0 12px",
          border: "none",
          borderTop: "1px solid rgba(42,58,31,.95)",
          background: "rgba(11,14,10,.92)",
          color: activeTool !== "select" ? C.violetBright : C.secondary,
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          fontFamily: "'Space Grotesk', sans-serif",
        }}
      >
        <span style={{ position: "relative", display: "inline-flex" }}>
          <FabIcon size={18} strokeWidth={1.75} />
          {hasSelection && (
            <span
              style={{
                position: "absolute",
                top: -3,
                right: -3,
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: C.violetBright,
                border: `2px solid ${C.panel1}`,
              }}
            />
          )}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.mutedB, flex: 1, textAlign: "left" }}>
          {active?.label ?? t.toolsFab}
        </span>
        <ChevronUp size={16} color={C.mutedB} />
      </button>
    );
  }

  return (
    <div
      style={{
        flex: "0 0 auto",
        width: "100%",
        boxSizing: "border-box",
        borderTop: "1px solid rgba(42,58,31,.95)",
        background: "rgba(11,14,10,.92)",
        fontFamily: "'Space Grotesk', sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "5px 8px 2px",
        }}
      >
        <span
          style={{
            color: C.mutedB,
            fontSize: 9.5,
            fontWeight: 800,
            letterSpacing: ".03em",
          }}
        >
          {active?.label ?? t.toolsHeader}
        </span>
        <button
          type="button"
          title={t.collapse}
          aria-label={t.collapse}
          onClick={() => setExpanded(false)}
          style={{
            width: 22,
            height: 22,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: 6,
            background: "transparent",
            color: C.mutedB,
            cursor: "pointer",
          }}
        >
          <ChevronDown size={15} />
        </button>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "2px 8px 8px",
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <ToolButton
          label={t.select}
          Icon={MousePointer2}
          active={activeTool === "select"}
          onClick={() => onTool("select")}
        />
        <ToolButton
          label={t.pan}
          Icon={Hand}
          active={activeTool === "pan"}
          onClick={() => onTool("pan")}
        />

        {TOOL_GROUPS.map((group) => (
          <React.Fragment key={group.key}>
            <VDivider />
            {group.tools.map(({ id, Icon }) => (
              <ToolButton
                key={id}
                active={activeTool === id}
                label={t.tools[id]}
                Icon={Icon}
                onClick={() => onTool(id)}
              />
            ))}
          </React.Fragment>
        ))}

        <VDivider />
        <IndicatorsControl indicators={indicators} onIndicator={onIndicator} openUpward t={t} />

        <ToolButton
          label={hasSelection ? t.deleteSelected : t.deleteSelectedDisabled}
          Icon={Trash2}
          disabled={!hasSelection}
          danger
          onClick={onDeleteSelected}
        />

        <VDivider />
        <ToolButton label={t.clearAll} Icon={Eraser} danger onClick={onClear} />
      </div>
    </div>
  );
}
